import { Component, Signal, signal } from '@angular/core';
import { CapitalContainer } from "@commons/capital-container/capital-container";
import { CapitalBack } from "@commons/capital-back/capital-back";
import { Instructions } from "./instructions/instructions";
import { CommonModule } from '@angular/common';
import { Stepper, StepData } from "./stepper/stepper";
import { AgileDeviceService } from '@commons/services/agile-device.service';
import { AuthService } from '@commons/services/auth.service';
import { BiometricAuthRequest } from '@commons/services/biometric-auth.types';
import { ClientData } from '@commons/services/agile-device.types';
import { Router } from '@angular/router';

@Component({
  selector: 'app-agile-login',
  imports: [CapitalContainer, CapitalBack, Instructions, CommonModule, Stepper],
  templateUrl: './agile-login.html',
  styleUrl: './agile-login.scss',
})
export class AgileLogin {
  isProccessStarted = signal(false);
  statusMessage = signal("Esperando cedula...");
  private clientData: ClientData | null = null;
  private readonly MACHINE_ID = this.generateMachineId();

  constructor(
    private agileService: AgileDeviceService, 
    private authService: AuthService,
    private router: Router
  ) {
    this.isAgileConnected = agileService.isConnected;
  }

  steps: StepData[] = [{
    subtitle: "Acerca tu cédula al lector. Asegúrate de colocar la parte frontal hacia arriba.",
    image: "/img/ci.png",
    action: (setStatus, nextStep, onError) => {
      setStatus("Esperando cedula");
      this.agileService.readOCR().subscribe((ocrResponse) => {
        if (ocrResponse.success && ocrResponse.data) {
          // Guardar los datos del cliente para usarlos en la autenticación biométrica
          this.clientData = ocrResponse.data;
          setStatus("Leyendo datos");
          this.agileService.readNFC(ocrResponse.data).subscribe((nfcResponse) => {
            if (nfcResponse.success)
              nextStep();
            else onError("Error al leer tu cedula", "Asegurate de que tu carnet este en buen estado y bien posicionado.")
          })
        } else
          onError("No fue posible detectar la cedula", "Asegurate de que tu carnet este en buen estado y bien posicionado.");
      });
    }
  }, {
    subtitle: "Coloca tu dedo índice en el lector.",
    image: "/img/fingerprint.svg",
    action: (setStatus, nextStep, onError) => {
      setStatus("Esperando huella");
      setTimeout(() => setStatus("No muevas tu dedo"), 2000);
      setTimeout(() =>
        this.agileService.readFingerprint().subscribe(async res => {
          if (res.success && res.data) {
            setStatus("Autenticando");
            try {
              // Construir el payload de autenticación biométrica
              const biometricPayload = this.buildBiometricPayload(res.data);
              
              // Llamar al endpoint de autenticación biométrica
              await this.authService.biometricLogin(biometricPayload);
              
              // Redirigir al usuario a home-options en caso de éxito
              this.router.navigate(["/home-options"]);
            } catch (error: any) {
              console.error('Error en autenticación biométrica:', error);
              onError("Error al autenticar", error.message || "No fue posible validar tu identidad");
            }
          } else {
            onError("Error al validar huella", "Asegurate de usar tu dedo indice y que el lector este limpio")
          }
        }), 2000)
    }
  }]

  isAgileConnected: undefined | Signal<boolean>;

  ngOnInit() {
    this.agileService.connect()
  }

  /**
   * Construye el payload de autenticación biométrica
   */
  private buildBiometricPayload(fingerprintData: any): BiometricAuthRequest {
    if (!this.clientData) {
      throw new Error('No se han leído los datos del cliente');
    }

    const now = new Date();
    const timestamp = now.toISOString();
    const id = parseInt(now.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14));

    const payload: BiometricAuthRequest = {
      code: 'PERSON',
      type: 'VERIFY',
      subtype: 'DOCUMENT',
      createdAt: timestamp,
      id: id,
      inResponseTo: 0,
      rut: this.clientData.rut,
      client: {
        operationId: 0,
        machineId: this.MACHINE_ID
      },
      data: {
        authenticationResult: fingerprintData.authenticationResult || {
          id: '1',
          type: 'MOC',
          subtype: 'MOC',
          authenticated: true,
          sign: fingerprintData.sign || '',
          method: 'CIDTray_MOC'
        },
        config: fingerprintData.config || {
          threshold: 50,
          maxCapture: 1,
          minQuality: 50,
          fingerId: '0',
          fingersId: ['0'],
          includeFacialImage: false,
          includeSignatureImage: false,
          rotationAngle: 0,
          widthCrop: 0,
          heightCrop: 0,
          fingerprintWidthCrop: 0,
          fingerprintHeightCrop: 0,
          imageVarianceThreshold: 0
        },
        info: fingerprintData.info || {
          deviceSerial: '',
          deviceType: 'FINGERPRINT_READER',
          deviceModel: '263',
          checkIdVersion: '1.0.33.0',
          solemBioVersion: '1.9.4.1'
        }
      },
      status: {
        idTransaction: 0,
        code: 0,
        message: 'NO_ERROR',
        update: false
      }
    };

    return payload;
  }

  /**
   * Genera un ID único de máquina
   */
  private generateMachineId(): string {
    // Generar un ID basado en características del navegador/dispositivo
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const platform = navigator.platform;
    const screenResolution = `${screen.width}x${screen.height}`;
    
    // Combinar características y crear un hash simple
    const combinedString = `${userAgent}-${language}-${platform}-${screenResolution}`;
    
    // Generar un ID alfanumérico único de 80 caracteres (similar al ejemplo)
    let machineId = '';
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    for (let i = 0; i < 80; i++) {
      const charIndex = (combinedString.charCodeAt(i % combinedString.length) + i) % chars.length;
      machineId += chars[charIndex];
    }
    
    return machineId;
  }
}
